import { useId } from "react"

import { Modal } from "../shared/Modal"
import {
  guestProfileModalCopy,
  type GuestProfileCategory,
} from "./aboutGuestsOptions"
import closeIconUrl from "./icons/close.svg"
import type { StayGuestProfileTraveller } from "./stayData"

type AboutGuestsEditFormProps = {
  canSave: boolean
  category: GuestProfileCategory
  error: string | null
  isSaving: boolean
  onClose: () => void
  onRemoveValue: (value: string) => void
  onSave: () => void
  traveller: StayGuestProfileTraveller
  values: readonly string[]
}

export function AboutGuestsEditForm({
  canSave,
  category,
  error,
  isSaving,
  onClose,
  onRemoveValue,
  onSave,
  traveller,
  values,
}: AboutGuestsEditFormProps) {
  const formId = useId()
  const modalCopy = guestProfileModalCopy[category.field]

  return (
    <Modal
      footer={(
        <>
          <button
            className="h-8 rounded-[4px] border border-[#344149] bg-white px-3 text-xs font-medium leading-4 text-[#344149] hover:bg-[#f5f5f5] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isSaving}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className={[
              "h-8 rounded-[4px] px-3 text-xs font-medium leading-4 text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed",
              canSave ? "bg-[#344149] hover:bg-[#263138]" : "bg-[#d2dadf]",
            ].join(" ")}
            disabled={!canSave}
            form={formId}
            type="submit"
          >
            Save
          </button>
        </>
      )}
      onClose={onClose}
      open
      title={modalCopy.editTitle}
    >
      <form
        className="flex flex-col gap-4"
        id={formId}
        onSubmit={(event) => {
          event.preventDefault()
          onSave()
        }}
      >
        <section className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-base font-medium uppercase leading-[19px] text-black">
              {traveller.display_name}
            </h3>
            <p className="text-xs font-normal leading-[15px] text-[#737373]">
              Remove the items that do not apply to this guest.
            </p>
          </div>

          <div
            aria-label={`${category.label} for ${traveller.display_name}`}
            className="flex flex-wrap gap-2.5"
          >
            {values.map((value) => (
              <button
                aria-label={`Remove ${value}`}
                className="inline-flex h-8 items-center gap-2 overflow-hidden whitespace-nowrap rounded-[24px] border border-[#d4d4d4] bg-[#f5f5f5] px-3 py-2 text-[13px] font-normal leading-normal text-black hover:border-[#344149] hover:bg-[#e7ebee] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isSaving}
                key={value}
                onClick={() => onRemoveValue(value)}
                type="button"
              >
                <span>{value}</span>
                <img alt="" className="block size-[8.17px]" src={closeIconUrl} />
              </button>
            ))}
          </div>
        </section>

        {error ? (
          <p className="rounded-[4px] border border-[#e0b4b4] bg-[#fff2f2] px-3 py-2 text-sm font-bold text-[#873230]">
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}
